import { Component, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { FancyAutocompleteComponent } from '../fancy-autocomplete/fancy-autocomplete.component';
import { FancyButtonComponent } from '../fancy-button/fancy-button.component';
import { FancyChipComponent } from '../fancy-chip/fancy-chip.component';
import { Client } from '../../../core/models/client.model';

/**
 * Client Selector Panel Component
 *
 * Dropdown panel for client selection on tablet/medium screens
 * Shows selected client chip and autocomplete for searching
 *
 * @example
 * ```html
 * <client-selector-panel
 *   [selectedClient]="selectedClient()"
 *   [isDefaultClient]="isDefaultClient()"
 *   [filteredClients]="filteredClients()"
 *   [searchControl]="clientSearchControl"
 *   (clientSelected)="onClientSelect($event)"
 *   (clientRemoved)="onClientRemove()"
 *   (addQuickClient)="onAddQuickClient()"
 * />
 * ```
 */
@Component({
  selector: 'client-selector-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    FancyAutocompleteComponent,
    FancyButtonComponent,
    FancyChipComponent,
  ],
  template: `
    <div class="client-selector-panel">
      <!-- Selected Client Display -->
      @if (selectedClient()) {
        <div class="client-selector-panel__selected">
          <mat-icon class="client-selector-panel__icon">person</mat-icon>
          <div class="client-selector-panel__info">
            <span class="client-selector-panel__label">Cliente seleccionado:</span>
            <div class="client-selector-panel__chip">
              <fancy-chip
                [variant]="isDefaultClient() ? 'default' : 'primary'"
                [icon]="'person'"
                [removable]="!isDefaultClient()"
                (removed)="clientRemoved.emit()"
              >
                {{ selectedClient()!.name }}
              </fancy-chip>
            </div>
          </div>
        </div>
      }

      <!-- Client Search -->
      <div class="client-selector-panel__search">
        <fancy-autocomplete
          [placeholder]="'Buscar cliente...'"
          [prefixIcon]="'search'"
          [options]="filteredClients()"
          [displayWith]="displayClientName"
          [size]="'md'"
          [glow]="true"
          [clearAfterSelect]="true"
          [disableFilter]="true"
          [formControl]="searchControl()"
          (optionSelected)="$event && clientSelected.emit($event)"
        >
          <ng-template #optionTemplate let-client>
            <div class="client-selector-panel__option">
              <mat-icon>person</mat-icon>
              <div class="client-selector-panel__option-info">
                <span class="client-selector-panel__option-name">{{ client.name }}</span>
                <span class="client-selector-panel__option-phone">{{ client.phone }}</span>
              </div>
            </div>
          </ng-template>
        </fancy-autocomplete>
      </div>

      <!-- Quick Add Button -->
      <div class="client-selector-panel__actions">
        <fancy-button
          [variant]="'ghost'"
          [size]="'md'"
          [icon]="'person_add'"
          [fullWidth]="true"
          (clicked)="addQuickClient.emit()"
        >
          Agregar cliente rápido
        </fancy-button>
      </div>
    </div>
  `,
  styleUrl: './client-selector-panel.component.scss',
})
export class ClientSelectorPanelComponent {
  /**
   * Selected client
   */
  selectedClient = input<Client | null>(null);

  /**
   * Whether the selected client is the default client
   */
  isDefaultClient = input<boolean>(false);

  /**
   * Filtered clients list for autocomplete
   */
  filteredClients = input<Client[]>([]);

  /**
   * Search form control
   */
  searchControl = input.required<FormControl>();

  /**
   * Client selected event
   */
  clientSelected = output<Client>();

  /**
   * Client removed event
   */
  clientRemoved = output<void>();

  /**
   * Add quick client event
   */
  addQuickClient = output<void>();

  /**
   * Display function for autocomplete
   */
  displayClientName(client: Client | null): string {
    return client ? client.name : '';
  }
}
